import { useMemo } from 'react'

import { Combobox } from '@/components/combobox'
import { Field, Input } from '@/components/field'
import { type BranchDraft, withTimezone } from '@/features/restaurants/branch-draft'
import { SlugField } from '@/features/restaurants/slug-field'
import type { SlugVerdict } from '@/features/restaurants/use-slug-verdict'
import { CURRENCIES, currencyName } from '@/lib/currencies'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { slugify } from '@/lib/slug'
import { timezoneOffset, timezoneOptions } from '@/lib/timezones'

/** Name + slug + zone + currency; shared by the create form's first branch and add-branch. */
export function BranchFields({
  value,
  onChange,
  slugVerdict,
  slugError,
  submitted,
}: {
  value: BranchDraft
  onChange: (value: BranchDraft) => void
  slugVerdict: SlugVerdict
  slugError?: unknown
  submitted: boolean
}) {
  const t = useT()
  const language = useLanguage()
  const zones = useMemo(
    () =>
      timezoneOptions().map((zone) => {
        const offset = timezoneOffset(zone)
        return { value: zone, label: offset ? `${zone} (${offset})` : zone }
      }),
    [],
  )
  const currencies = useMemo(() => CURRENCIES.map((code) => ({ value: code, label: `${code} · ${currencyName(code, language)}` })), [language])

  return (
    <>
      <Field label={t('restaurants.branchName')}>
        <Input
          required
          value={value.name}
          onChange={(e) => {
            const name = e.target.value
            onChange({ ...value, name, slug: value.slugEdited ? value.slug : slugify(name) })
          }}
        />
      </Field>
      <SlugField
        label={t('restaurants.branchSlug')}
        value={value.slug}
        onChange={(slug) => onChange({ ...value, slug, slugEdited: true })}
        verdict={slugVerdict}
        submitted={submitted}
        error={slugError}
        hint={t('restaurants.branchSlugHint')}
      />
      <Field label={t('restaurants.timezone')}>
        <Combobox value={value.timezone} onChange={(timezone) => onChange(withTimezone(value, timezone))} options={zones} />
      </Field>
      <Field label={t('restaurants.currency')} hint={t('restaurants.currencyHint')}>
        <Combobox value={value.currency} onChange={(currency) => onChange({ ...value, currency, currencyEdited: true })} options={currencies} />
      </Field>
    </>
  )
}
